import React, { useState } from 'react';
import type { ReviewEnrollmentViewProps } from 'interfaces/enrollments/ReviewEnrollmentViewProps';
import { ReviewDecision } from 'models/submissions/ReviewDecision';
import type { ReviewDecisionSubmission } from 'models/submissions/ReviewDecisionSubmission';

const notesRequiredDecisions: ReviewDecision[] = [ReviewDecision.Rejected, ReviewDecision.RequestChanges];

const parseInstructions = (json?: string): string => {
  if (!json) return '';
  try {
    const parsed = JSON.parse(json);
    if (typeof parsed === 'string') return parsed;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return json;
  }
};

const decisionLabel: Record<ReviewDecision, string> = {
  [ReviewDecision.Approved]: 'Approve',
  [ReviewDecision.Rejected]: 'Reject',
  [ReviewDecision.RequestChanges]: 'Request Changes',
};

const decisionButtonClass: Record<ReviewDecision, string> = {
  [ReviewDecision.Approved]:
    'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500',
  [ReviewDecision.Rejected]:
    'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
  [ReviewDecision.RequestChanges]:
    'bg-yellow-500 hover:bg-yellow-600 text-white focus:ring-yellow-400',
};

export const ReviewEnrollmentView: React.FC<ReviewEnrollmentViewProps> = ({
  enrollment,
  onSubmit,
  isDisabled = false,
}) => {
  const [pendingDecision, setPendingDecision] = useState<ReviewDecision | null>(null);
  const [notes, setNotes] = useState('');

  const instructions = parseInstructions(enrollment.reviewInstructionsJson);

  const handleDecisionClick = (decision: ReviewDecision) => {
    if (notesRequiredDecisions.includes(decision)) {
      setPendingDecision(decision);
      setNotes('');
    } else {
      submitDecision(decision, undefined);
    }
  };

  const submitDecision = (decision: ReviewDecision, notesJson: string | undefined) => {
    const submission: ReviewDecisionSubmission = {
      id: '',
      enrollmentId: enrollment.id,
      nodeInstanceId: enrollment.nodeInstanceId ?? '',
      workflowInstanceId: '',
      submittedByUserId: '',
      type: 'ReviewDecision',
      submittedAt: new Date().toISOString(),
      decision,
      notesJson: notesJson ? JSON.stringify({ notes: notesJson }) : undefined,
    };
    onSubmit(submission);
  };

  const handleConfirmNotes = () => {
    if (!pendingDecision) return;
    submitDecision(pendingDecision, notes);
    setPendingDecision(null);
    setNotes('');
  };

  const handleCancelNotes = () => {
    setPendingDecision(null);
    setNotes('');
  };

  return (
    <div className="p-4 border rounded bg-white shadow-sm space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Review Instructions</h3>
        {instructions ? (
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{instructions}</p>
        ) : (
          <p className="text-sm text-gray-400 italic">No instructions provided.</p>
        )}
      </div>

      {pendingDecision ? (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Notes <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full border rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Enter notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isDisabled}
          />
          <div className="flex gap-2">
            <button
              onClick={handleConfirmNotes}
              disabled={isDisabled || !notes.trim()}
              className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Confirm {decisionLabel[pendingDecision]}
            </button>
            <button
              onClick={handleCancelNotes}
              disabled={isDisabled}
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap">
          {[ReviewDecision.Approved, ReviewDecision.Rejected, ReviewDecision.RequestChanges].map((decision) => (
            <button
              key={decision}
              onClick={() => handleDecisionClick(decision)}
              disabled={isDisabled}
              className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 ${decisionButtonClass[decision]}`}
            >
              {decisionLabel[decision]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewEnrollmentView;
